import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Clock, X, Eye } from 'lucide-react';
import { useAnalysisStatus } from '../hooks/useAnalysisStatus';
import { useAnalysisContext } from '../contexts/AnalysisContext';

interface AnalysisStatusIndicatorProps {
  analysisId: string;
  onViewReport: () => void;
  onDismiss: () => void;
}

const AnalysisStatusIndicator: React.FC<AnalysisStatusIndicatorProps> = ({
  analysisId,
  onViewReport,
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const { updateAnalysis } = useAnalysisContext();
  
  const { status, isPolling, error } = useAnalysisStatus({
    analysisId,
    enableNotifications: true,
    backgroundMode: true,
    onComplete: () => {
      // Keep indicator visible when complete
      setIsMinimized(false);
      // Mark this analysis as unread to trigger navbar badge
      updateAnalysis(analysisId, { status: 'completed', progress: 100, unread: true });
    },
    onNavigate: (path) => {
      if (path === '/report') {
        window.location.href = `/report/${analysisId}`;
      } else {
        window.location.href = path;
      }
    }
  });

  // Auto-hide after 5 seconds if there's an error
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, onDismiss]);

  // Don't render if no analysisId
  if (!analysisId) {
    return null;
  }

  if (!isVisible) return null;

  const getStatusIcon = () => {
    if (error) return <AlertCircle className="h-5 w-5 text-red-500" />;
    if (status?.status === 'completed') return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (isPolling) return <Clock className="h-5 w-5 text-blue-500 animate-pulse" />;
    return <Clock className="h-5 w-5 text-gray-500" />;
  };

  const getStatusText = () => {
    if (error) return 'Analysis Error';
    if (status?.status === 'completed') return 'Analysis Complete!';
    if (isPolling) return `Processing... ${status?.progress || 0}%`;
    return 'Starting Analysis...';
  };

  const getStatusColor = () => {
    if (error) return 'border-red-200 bg-red-50';
    if (status?.status === 'completed') return 'border-green-200 bg-green-50';
    if (isPolling) return 'border-blue-200 bg-blue-50';
    return 'border-gray-200 bg-gray-50';
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
      isMinimized ? 'w-12 h-12' : 'w-80'
    }`}>
      <div className={`rounded-lg shadow-lg border-2 ${getStatusColor()} p-4 transition-all duration-300`}>
        {isMinimized ? (
          <button
            onClick={() => setIsMinimized(false)}
            className="w-full h-full flex items-center justify-center"
            title="Click to expand"
          >
            {getStatusIcon()}
          </button>
        ) : (
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusIcon()}
                <span className="font-semibold text-sm text-gray-900">
                  {getStatusText()}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Minimize"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  onClick={() => {
                    setIsVisible(false);
                    onDismiss();
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Dismiss"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            {isPolling && status && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${status.progress || 0}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600">
                  {status.message || 'Processing your video...'}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              {status?.status === 'completed' && (
                <button
                  onClick={onViewReport}
                  className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  <span>View Report</span>
                </button>
              )}
              
              {error && (
                <button
                  onClick={() => {
                    setIsVisible(false);
                    onDismiss();
                  }}
                  className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                >
                  Dismiss
                </button>
              )}
            </div>

            {/* Analysis ID */}
            <p className="text-xs text-gray-500 font-mono">
              ID: {analysisId.substring(0, 8)}...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisStatusIndicator;
